import { z } from 'zod';
import {
    BIA_SYSTEM_CATEGORY_KEYS,
    BIA_GUIDANCE_SEVERITY_KEYS,
    BIA_CALCULATION_MODELS,
    BIA_TIME_UNITS,
} from '@repo/bia-impact-engine';

export const BIA_CATEGORY_KEY_SLUG_REGEX = /^[a-z][a-z0-9_]{2,48}$/;

export const BiaCategoryKeySlugSchema = z.string().regex(
    BIA_CATEGORY_KEY_SLUG_REGEX,
    'Category key must be 3–49 lowercase letters, digits, or underscores',
);

export const BiaCategoryLocalizedLabelSchema = z.record(z.string(), z.string()).refine(
    (value) => typeof value.en === 'string' && value.en.trim().length > 0,
    { message: 'Özel kategori için İngilizce etiket (en) zorunludur' },
);

export const BiaCategorySourceSchema = z.enum(['system', 'custom']);

export const BiaGuidanceSeverityKeySchema = z.enum([
    'NEGLIGIBLE',
    'MINOR',
    'MODERATE',
    'MAJOR',
    'SEVERE',
]);

export const BiaCategorySeverityDescriptionsSchema = z.object({
    NEGLIGIBLE: z.string().max(1000).optional(),
    MINOR: z.string().max(1000).optional(),
    MODERATE: z.string().max(1000).optional(),
    MAJOR: z.string().max(1000).optional(),
    SEVERE: z.string().max(1000).optional(),
}).strict().partial().optional();

/** @deprecated Use BiaCategoryKeySlugSchema — system keys are a subset of slugs */
export const BiaCategoryKeySchema = BiaCategoryKeySlugSchema;

export const BiaCategoryConfigSchema = z.object({
    categoryKey: BiaCategoryKeySlugSchema,
    weight: z.number().positive().optional(),
    label: BiaCategoryLocalizedLabelSchema.optional(),
    source: BiaCategorySourceSchema.optional(),
    severityDescriptions: BiaCategorySeverityDescriptionsSchema,
});

export const BiaTimeHorizonSchema = z.object({
    id: z.string().min(1),
    value: z.number().positive(),
    unit: z.enum(BIA_TIME_UNITS),
    sortOrder: z.number().int().nonnegative().optional(),
});

export const BiaCalculationModelSchema = z.enum(BIA_CALCULATION_MODELS);

function isSystemCategoryKey(categoryKey) {
    return BIA_SYSTEM_CATEGORY_KEYS.includes(categoryKey);
}

export const BiaImpactConfigurationPayloadSchema = z.object({
    isCustomized: z.boolean().optional(),
    categories: z.array(BiaCategoryConfigSchema).min(1),
    timeHorizons: z.array(BiaTimeHorizonSchema).min(1),
    calculationModel: BiaCalculationModelSchema,
    mtpdThreshold: z.number().min(0).max(5),
    rtoThreshold: z.number().min(0).max(5),
    rpoThreshold: z.number().min(0).max(5),
    resetToDefaults: z.boolean().optional(),
    customFields: z.record(z.unknown()).optional(),
}).superRefine((data, ctx) => {
    const categoryKeys = data.categories.map((c) => c.categoryKey);
    if (new Set(categoryKeys).size !== categoryKeys.length) {
        ctx.addIssue({ code: 'custom', message: 'Duplicate category keys are not allowed', path: ['categories'] });
    }

    data.categories.forEach((cat, i) => {
        const system = isSystemCategoryKey(cat.categoryKey);
        if (system) {
            if (cat.label && Object.keys(cat.label).length > 0) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'System categories cannot have custom labels',
                    path: ['categories', i, 'label'],
                });
            }
            if (cat.source === 'custom') {
                ctx.addIssue({
                    code: 'custom',
                    message: 'System category cannot use custom source',
                    path: ['categories', i, 'source'],
                });
            }
        } else if (!cat.label?.en?.trim()) {
            ctx.addIssue({
                code: 'custom',
                message: 'Custom category label (en) is required',
                path: ['categories', i, 'label'],
            });
        }

        if (cat.severityDescriptions) {
            for (const key of Object.keys(cat.severityDescriptions)) {
                if (!BIA_GUIDANCE_SEVERITY_KEYS.includes(key)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: `Unknown severity description key: ${key}`,
                        path: ['categories', i, 'severityDescriptions', key],
                    });
                }
            }
        }
    });

    const durationSet = new Set();
    for (const [i, horizon] of data.timeHorizons.entries()) {
        const minutes = horizon.unit === 'minutes' ? horizon.value
            : horizon.unit === 'hours' ? horizon.value * 60
                : horizon.unit === 'days' ? horizon.value * 60 * 24
                    : horizon.value * 60 * 24 * 7;
        if (durationSet.has(minutes)) {
            ctx.addIssue({ code: 'custom', message: 'Duplicate time horizon duration', path: ['timeHorizons', i] });
        }
        durationSet.add(minutes);
    }

    if (data.calculationModel === 'WEIGHTED_AVERAGE') {
        data.categories.forEach((cat, i) => {
            if (cat.weight == null || cat.weight <= 0) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Weight is required for weighted average',
                    path: ['categories', i, 'weight'],
                });
            }
        });
    }

    if (data.rtoThreshold > data.mtpdThreshold) {
        ctx.addIssue({
            code: 'custom',
            message: 'RTO threshold cannot exceed MTPD threshold',
            path: ['rtoThreshold'],
        });
    }

    if (data.rpoThreshold > data.rtoThreshold) {
        ctx.addIssue({
            code: 'custom',
            message: 'RPO threshold cannot exceed RTO threshold',
            path: ['rpoThreshold'],
        });
    }
});

export const BiaSeverityKeySchema = z.enum([
    'NO_IMPACT',
    'NEGLIGIBLE',
    'MINOR',
    'MODERATE',
    'MAJOR',
    'SEVERE',
]);

export const BiaImpactScoreRowSchema = z.object({
    categoryKey: z.string().min(1),
    values: z.record(BiaSeverityKeySchema),
});

export const BiaImpactAssessmentInputSchema = z.object({
    scores: z.array(BiaImpactScoreRowSchema),
});

export const BiaImpactConfigurationResponseSchema = z.object({
    success: z.boolean(),
    data: z.unknown(),
    message: z.string().optional(),
});
